import { companyIdOf, companyWorkspaceEntry, scopedQuotes } from '../components/job-workspace/workspaceScope';
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Search, Plus, Mail, LayoutGrid, List as ListIcon, Trash2, Edit3, X } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';
import { Button } from '../components/ui/Button';
import { PipelineItem, Quote } from '../types';
import { supabase } from '../supabaseClient';

interface CompanyProps {
  companies: any[];
  crmItems: PipelineItem[];
  quotes: Quote[];
  onRefresh: () => Promise<void>;
  onOpenQuote: (quote: Quote) => void;
  onViewInquiry: (item: any) => void;
  onQuoteAction?: (item?: any) => void;
}

const Companies = ({ companies = [], crmItems = [], quotes = [], onRefresh, onViewInquiry }: CompanyProps) => {
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // ⚡ DUAL-PURPOSE MODE TRACKER
  const [editingCompany, setEditingCompany] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  const [newCompany, setNewCompany] = useState({
    name: '',
    industry: '',
    website: '',
    accounts_payable_email: ''
  });

  const companyAnalyticData = useMemo(() => {
    return companies.map(company => {
      // 1. First, gather all leads tied to this company
      const linkedLeads = crmItems.filter(item => companyIdOf(item) === company.id);
      const linkedQuotes = scopedQuotes(quotes, linkedLeads, company.id, null, 'Whole Company');
      
      // 3. ⚡ THE FIX: Count BOTH Approved and Completed jobs!
      const lifetimeValue = linkedQuotes
        .filter(q => ['APPROVED', 'COMPLETED'].includes(String(q.status).toUpperCase()))
        .reduce((sum, q) => sum + (Number(q.subtotal || (q as any).amount || (q as any).value) || 0), 0);
      
      return {
        ...company,
        stats: {
          leadsCount: linkedLeads.length,
          quotesCount: linkedQuotes.length,
          projectsCount: linkedLeads.filter((l: any) => {
            const stage = String(l.status || l.productionStage || l.salesStage || '').toUpperCase();
            return ['PRE_PRODUCTION', 'STAGING', 'LIVE_EVENT', 'LIVE', 'POST_EVENT', 'COMPLETED', 'COMPLETE', 'UPCOMING', 'WON'].includes(stage);
          }).length,
          ltv: lifetimeValue,
          lastActive: linkedLeads[0]?.lastUpdated || (linkedQuotes[0] as any)?.updated_at || 'No recent activity'
        },
        allQuotes: linkedQuotes
      };
    }).filter(c => {
        const matchesSearch = c.name?.toLowerCase().includes(search.toLowerCase()) || c.accounts_payable_email?.toLowerCase().includes(search.toLowerCase());
        const matchesIndustry = industryFilter === 'All' || c.industry === industryFilter;
        return matchesSearch && matchesIndustry;
    });
  }, [companies, crmItems, quotes, search, industryFilter]);

  const industries = useMemo(() => {
    const set = new Set(companies.map(c => c.industry).filter(Boolean));
    return ['All', ...Array.from(set)];
  }, [companies]);

  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        name: newCompany.name.trim(),
        industry: newCompany.industry || 'General',
        website: newCompany.website || '',
        accounts_payable_email: newCompany.accounts_payable_email.trim().toLowerCase() || null
      };

      if (editingCompany) {
        // ⚡ UPDATE RUNTIME
        const { error } = await supabase.from('companies').update(payload).eq('id', editingCompany.id);
        if (error) throw error;
        alert("Company record updated smoothly!");
      } else {
        // ⚡ INSERT RUNTIME
        const { error } = await supabase.from('companies').insert([payload]);
        if (error) throw error;
        alert("Company successfully registered!");
      }

      await onRefresh();
      setIsModalOpen(false);
      setEditingCompany(null);
      setNewCompany({ name: '', industry: '', website: '', accounts_payable_email: '' });
    } catch (err: any) {
      console.error("Database save exception:", err);
      alert(`Save Failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEdit = (e: React.MouseEvent, company: any) => {
    e.stopPropagation();
    setEditingCompany(company);
    setNewCompany({
      name: company.name || '',
      industry: company.industry || '',
      website: company.website || '',
      accounts_payable_email: company.accounts_payable_email || ''
    });
    setIsModalOpen(true);
  };

  const handleDeleteCompany = async (id: string) => {
    if (!window.confirm("Remove this entity?")) return;
    try {
        const { error } = await supabase.from('companies').delete().eq('id', id);
        if (error) throw error;
        await onRefresh();
    } catch (err) { console.error(err); }
  };

  const handleDeepScan = (company: any) => {
    onViewInquiry(companyWorkspaceEntry(company));
  };

  return (
    <div className="animate-fade-in-up space-y-12 pb-12">
      {/* --- HEADER --- */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 lg:gap-8">
        <div>
          <h1 className="type-page-title text-white">Entity Portfolio</h1>
          <p className="type-label mt-2 flex items-center gap-2 text-slate-500 md:mt-3">
            <Building2 size={12} className="text-brand-neonBlue" /> {companies.length} Linked Organizations
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-center">
          <div className="relative w-full sm:flex-1 lg:min-w-[300px]">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18}/>
            <input 
              className="w-full bg-slate-900/60 border border-slate-700/50 rounded-xl md:rounded-[22px] py-3 md:py-4 pl-12 pr-6 text-white focus:border-brand-neonBlue outline-none shadow-2xl backdrop-blur-xl transition-all text-sm font-medium" 
              placeholder="Search by identity or billing email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          
          <div className="flex bg-slate-900/80 rounded-xl md:rounded-[22px] p-1 border border-white/10 backdrop-blur-xl shadow-2xl w-full sm:w-auto">
             <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg sm:p-2.5 transition-all flex items-center justify-center flex-1 sm:flex-none ${viewMode === 'grid' ? 'bg-brand-neonBlue text-slate-950 shadow-neon-blue' : 'text-slate-500 hover:text-white hover:bg-white/5'}`} title="Card View"><LayoutGrid size={16} /></button>
             <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg sm:p-2.5 transition-all flex items-center justify-center flex-1 sm:flex-none ${viewMode === 'list' ? 'bg-brand-neonBlue text-slate-950 shadow-neon-blue' : 'text-slate-500 hover:text-white hover:bg-white/5'}`} title="List View"><ListIcon size={16} /></button>
          </div>

          <Button onClick={() => { setEditingCompany(null); setNewCompany({ name: '', industry: '', website: '', accounts_payable_email: '' }); setIsModalOpen(true); }} className="bg-brand-neonBlue text-slate-950 shadow-neon-blue px-6 py-3.5 rounded-xl md:rounded-[22px] font-bold transition-transform hover:scale-105 w-full sm:w-auto whitespace-nowrap text-sm">
            <Plus size={18} className="mr-2" /> New Entity
          </Button>
        </div>
      </div>

      {/* --- GRID VIEW (CARDS) --- */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-10">
            {companyAnalyticData.map(company => (
              <GlassCard key={company.id} className="p-0 overflow-hidden group hover:border-brand-neonBlue/40 transition-all duration-700 rounded-3xl md:rounded-[48px] flex flex-col h-full shadow-3xl">
                <div className="p-6 border-b border-white/5 bg-gradient-to-br from-white/[0.03] to-transparent relative">
                  <div className="absolute top-6 right-6 flex gap-2 opacity-100 md:opacity-0 group-hover:opacity-100 transition-all">
                      {/* ⚡ NEW EDIT TRIGGER BUTTON */}
                      <button onClick={(e) => handleOpenEdit(e, company)} className="p-2 bg-slate-800 border border-white/10 rounded-xl text-slate-400 hover:text-brand-neonBlue hover:bg-slate-700 transition-all" title="Edit Properties"><Edit3 size={14}/></button>
                      <button onClick={() => handleDeleteCompany(company.id)} className="p-2 bg-red-500/10 rounded-xl text-red-500 hover:bg-red-500 hover:text-white transition-all"><Trash2 size={14}/></button>
                  </div>
                  <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center text-brand-neonBlue border border-white/10 group-hover:border-brand-neonBlue/30 mb-6 shadow-inner"><Building2 size={24} /></div>
                  <h4 className="type-heading line-clamp-2 text-base leading-snug text-white transition-colors group-hover:text-brand-neonBlue md:text-lg">{company.name}</h4>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="type-label rounded-full border border-white/5 bg-white/5 px-3 py-1 text-slate-400">{company.industry || 'Market Partner'}</span>
                    {company.accounts_payable_email && (
                      <span className="flex min-w-0 items-center gap-1 rounded-full border border-brand-neonPink/20 bg-brand-neonPink/10 px-3 py-1 text-xs text-brand-neonPink">
                        <Mail size={10}/> {company.accounts_payable_email}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="p-6 space-y-6 flex-1">
                  <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-1"><p className="type-label text-slate-500">Deployments</p><p className="type-numeric text-2xl text-white">{company.stats.projectsCount}</p></div>
                      <div className="space-y-1 text-right"><p className="type-label text-slate-500">Account LTV</p><p className="type-numeric text-2xl text-brand-neonBlue">${company.stats.ltv.toLocaleString()}</p></div>
                  </div>
                </div>

                <div className="px-6 py-4 bg-slate-950/40 border-t border-white/5 flex justify-between items-center gap-4">
                   <div><p className="type-label text-slate-500">Recent Movement</p><p className="mt-0.5 text-xs font-semibold text-slate-400">{company.stats.lastActive}</p></div>
                   <Button variant="outline" size="sm" className="rounded-xl border-white/10 px-6 py-2.5 text-xs group-hover:bg-brand-neonBlue group-hover:text-slate-950" onClick={() => handleDeepScan(company)}>
                      Deep Scan
                    </Button>
                </div>
              </GlassCard>
            ))}
        </div>
      )}

      {/* --- LIST VIEW (TABLE) --- */}
      {viewMode === 'list' && (
        <div className="bg-slate-900/40 rounded-[48px] border border-white/5 overflow-hidden backdrop-blur-3xl shadow-[0_40px_100px_rgba(0,0,0,0.4)] relative">
          <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left border-collapse min-w-[900px]">
              <thead className="type-table-heading bg-slate-950/80 text-slate-500">
                <tr>
                  <th className="p-8">Organization Context</th>
                  <th className="p-8 text-center">Deployments</th>
                  <th className="p-8 text-center">Proposals</th>
                  <th className="p-8 text-right">Account LTV</th>
                  <th className="p-8 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {companyAnalyticData.map(company => (
                  <tr key={company.id} className="hover:bg-brand-neonBlue/[0.02] text-slate-300 transition-all duration-500 group">
                    <td className="p-8">
                      <div className="flex items-center gap-6">
                        <div className="w-12 h-12 bg-slate-800 rounded-[20px] flex items-center justify-center text-brand-neonBlue border border-white/5 group-hover:border-brand-neonBlue/30 transition-all shrink-0">
                          <Building2 size={20} />
                        </div>
                        <div>
                          <p className="text-lg font-bold text-white tracking-tight group-hover:text-brand-neonBlue transition-colors">{company.name}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs font-semibold text-slate-500">
                            <span>{company.industry || 'Market Partner'}</span>
                            {company.accounts_payable_email && (
                              <>
                                <span className="text-slate-700 font-normal">•</span>
                                <span className="flex items-center gap-1 rounded border border-brand-neonPink/10 bg-brand-neonPink/5 px-2 py-0.5 text-brand-neonPink">
                                  <Mail size={10}/> AP Inbox: {company.accounts_payable_email}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-8 text-center font-bold text-white text-lg">{company.stats.projectsCount}</td>
                    <td className="p-8 text-center font-bold text-white text-lg">{company.stats.quotesCount}</td>
                    <td className="type-numeric p-8 text-right text-lg text-brand-neonBlue">
                      ${company.stats.ltv.toLocaleString()}
                    </td>
                    <td className="p-8">
                      <div className="flex justify-center items-center gap-3 opacity-0 group-hover:opacity-100 transition-all">
                        <Button size="sm" variant="outline" onClick={() => handleDeepScan(company)} className="rounded-2xl border-white/10 hover:bg-brand-neonBlue hover:text-slate-950 transition-all py-3 px-6 font-bold uppercase text-[10px] tracking-widest">Deep Scan</Button>
                        {/* ⚡ INSTANT ROW EDIT PENCIL */}
                        <button onClick={(e) => handleOpenEdit(e, company)} className="p-3 bg-slate-800 border border-white/10 rounded-2xl text-slate-400 hover:text-brand-neonBlue hover:bg-slate-700 transition-all"><Edit3 size={16}/></button>
                        <button onClick={() => handleDeleteCompany(company.id)} className="p-3 bg-red-500/10 rounded-2xl text-red-500 hover:bg-red-500 hover:text-white transition-all"><Trash2 size={16}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

{/* --- DUAL CREATE/EDIT MODAL --- */}
      {isModalOpen && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm" onClick={() => { setIsModalOpen(false); setEditingCompany(null); }}>
              {/* ⚡ HTML SHIELD DIV: Standard HTML elements accept the event object, keeping TypeScript happy while stopping backdrop click-aways */}
              <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl animate-fade-in-up">
                  <GlassCard className="w-full p-6 md:p-10 border-brand-neonBlue/30 shadow-neon-blue rounded-[32px] max-h-[90vh] overflow-y-auto custom-scrollbar">
                      <div className="flex justify-between items-center mb-6 md:mb-10">
                          <h3 className="type-heading text-2xl text-white md:text-3xl">
                            {editingCompany ? `Modify Context: ${editingCompany.name}` : 'Register Entity'}
                          </h3>
                          <button type="button" onClick={() => { setIsModalOpen(false); setEditingCompany(null); }} className="text-slate-500 hover:text-white transition-all"><X size={32}/></button>
                      </div>
                      <form onSubmit={handleSaveCompany} className="space-y-6">
                          <div className="space-y-2">
                              <label className="type-label ml-2 text-slate-500">Company Name *</label>
                              <input required className="w-full bg-slate-900 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-brand-neonBlue transition-colors" value={newCompany.name} onChange={e => setNewCompany({...newCompany, name: e.target.value})} placeholder="e.g. Property Brokers" />
                          </div>
                          
                          <div className="space-y-2">
                              <label className="type-label ml-2 text-slate-500">Market Sector</label>
                              <input className="w-full bg-slate-900 border border-slate-800 rounded-2xl p-5 text-white outline-none focus:border-brand-neonBlue transition-colors" value={newCompany.industry} onChange={e => setNewCompany({...newCompany, industry: e.target.value})} placeholder="e.g. Real Estate" />
                      </div>

                      <div className="space-y-2">
                          <label className="type-label ml-2 flex items-center gap-1 text-brand-neonPink">
                            <Mail size={12}/> Accounts Payable Email (Xero Sync Pipeline)
                          </label>
                          <input 
                            type="email"
                            className="w-full rounded-2xl border border-brand-neonPink/30 bg-slate-900/50 p-5 text-white shadow-[0_0_15px_rgba(236,72,153,0.05)] outline-none transition-all placeholder:text-slate-600 focus:border-brand-neonPink"
                            value={newCompany.accounts_payable_email} 
                            onChange={e => setNewCompany({...newCompany, accounts_payable_email: e.target.value})} 
                            placeholder="e.g. invoices@propertybrokers.co.nz" 
                          />
                      </div>

                      <div className="flex flex-col-reverse sm:flex-row gap-4 pt-4 border-t border-white/5">
                          <Button variant="outline" className="flex-1 py-4 rounded-xl" onClick={() => { setIsModalOpen(false); setEditingCompany(null); }}>Discard</Button>
                          <Button type="submit" disabled={isSubmitting} className="flex-1 py-4 bg-brand-neonBlue text-slate-950 font-black uppercase tracking-widest rounded-xl shadow-neon-blue border-none">
                              {isSubmitting ? 'Saving...' : (editingCompany ? 'Save Modifications' : 'Confirm Registration')}
                          </Button>
                      </div>
                  </form>
              </GlassCard>
            </div>
          </div>,
          document.body
      )}
    </div>
  );
};

export default Companies;
